// Shared helpers for formatting stock data
const CURRENCY_SYMBOL = {
  USD: "$",
  INR: "₹",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
};

export const symbolFor = (currency) =>
  CURRENCY_SYMBOL[(currency || "USD").toUpperCase()] || "$";

export const fmtMoney = (n, currency = "USD", d = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const sym = symbolFor(currency);
  return `${sym}${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  })}`;
};

// Legacy helper — defaults to USD. Prefer fmtMoney(n, currency) for mixed-market UIs.
export const fmtCurrency = (n, d = 2) => fmtMoney(n, "USD", d);

export const fmtLarge = (n, currency = "USD") => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const sym = symbolFor(currency);
  const num = Number(n);
  // INR market caps commonly in crores (1 cr = 10M) but yfinance returns raw INR
  if (currency === "INR") {
    if (num >= 1e12) return `${sym}${(num / 1e12).toFixed(2)}L Cr`;
    if (num >= 1e7) return `${sym}${(num / 1e7).toFixed(2)} Cr`;
    if (num >= 1e5) return `${sym}${(num / 1e5).toFixed(2)} L`;
    return `${sym}${num.toLocaleString()}`;
  }
  if (num >= 1e12) return `${sym}${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `${sym}${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${sym}${(num / 1e6).toFixed(2)}M`;
  return `${sym}${num.toLocaleString()}`;
};

export const fmtPct = (n, d = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(d)}%`;
};

export const fmtRatio = (n, d = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toFixed(d);
};

export const verdictStyle = (verdict) => {
  const map = {
    UNDERVALUED: "bg-moss-bg text-moss-light border-moss-light/30",
    OVERVALUED: "bg-terracotta-bg text-terracotta border-terracotta/30",
    HOLD: "bg-amber_brand-bg text-amber_brand border-amber_brand/30",
    FAIRLY_VALUED: "bg-amber_brand-bg text-amber_brand border-amber_brand/30",
  };
  return map[verdict] || map.HOLD;
};

export const marginColor = (mos) => {
  if (mos === null || mos === undefined) return "text-stone_brand-dark";
  if (mos >= 15) return "text-moss-light";
  if (mos <= -10) return "text-terracotta";
  return "text-amber_brand";
};

// ---- Market / ticker helpers ----
export const MARKETS = [
  { key: "US", label: "US", suffix: "", hint: "NASDAQ/NYSE — e.g. AAPL" },
  { key: "NSE", label: "NSE", suffix: ".NS", hint: "India NSE — e.g. RELIANCE" },
  { key: "BSE", label: "BSE", suffix: ".BO", hint: "India BSE — e.g. TATASTEEL" },
];

export const normalizeTicker = (raw, marketKey = "US") => {
  if (!raw) return "";
  const t = raw.trim().toUpperCase();
  // Already has a suffix
  if (t.includes(".")) return t;
  const m = MARKETS.find((x) => x.key === marketKey);
  return m && m.suffix ? `${t}${m.suffix}` : t;
};

export const marketFromTicker = (ticker) => {
  if (!ticker) return "US";
  const t = ticker.toUpperCase();
  if (t.endsWith(".NS")) return "NSE";
  if (t.endsWith(".BO")) return "BSE";
  return "US";
};

export const displayTicker = (ticker) => {
  // Strip .NS / .BO for cleaner presentation; badge shows market separately.
  if (!ticker) return "";
  return ticker.replace(/\.(NS|BO)$/i, "");
};

// ---- External research deep-links ----
// For Indian stocks: links into Screener, Trendlyne, Value Research, Motilal Oswal,
// ET Money, INDmoney, Groww, Angel One, Zerodha.
// For US stocks: Morningstar, Yahoo Finance, Seeking Alpha, SEC EDGAR.
export const externalResearchLinks = (ticker) => {
  const base = displayTicker(ticker);
  const isIndian = /\.(NS|BO)$/i.test(ticker || "");

  if (!isIndian) {
    return [
      { name: "Morningstar", url: `https://www.morningstar.com/stocks/xnas/${base.toLowerCase()}/quote`, color: "bg-[#EC1C24]/10 text-[#B8101A] border-[#B8101A]/30" },
      { name: "Yahoo Finance", url: `https://finance.yahoo.com/quote/${base}`, color: "bg-[#6001D2]/10 text-[#4A00A8] border-[#4A00A8]/30" },
      { name: "Seeking Alpha", url: `https://seekingalpha.com/symbol/${base}`, color: "bg-[#F7941D]/10 text-[#C2710F] border-[#C2710F]/30" },
      { name: "SEC EDGAR", url: `https://efts.sec.gov/LATEST/search-index?q=%22${base}%22&dateRange=custom&forms=10-K`, color: "bg-stone_brand-dark/10 text-stone_brand-dark border-stone_brand-dark/30" },
      { name: "Google Finance", url: `https://www.google.com/finance/quote/${base}:NASDAQ`, color: "bg-[#4285F4]/10 text-[#1A56CC] border-[#1A56CC]/30" },
    ];
  }

  return [
    { name: "Screener.in",    url: `https://www.screener.in/company/${base}/`,                       color: "bg-[#2C7BE5]/10 text-[#1E5AB0] border-[#1E5AB0]/30" },
    { name: "Trendlyne",      url: `https://trendlyne.com/fundamentals/ticker/${base}/`,             color: "bg-[#0891B2]/10 text-[#066A85] border-[#066A85]/30" },
    { name: "Value Research", url: `https://www.valueresearchonline.com/stocks/?q=${base}`,          color: "bg-[#E63946]/10 text-[#B12630] border-[#B12630]/30" },
    { name: "Motilal Oswal",  url: `https://www.motilaloswal.com/search?query=${base}`,              color: "bg-[#F59E0B]/10 text-[#B26E04] border-[#B26E04]/30" },
    { name: "ET Money",       url: `https://www.etmoney.com/search?search=${base}`,                  color: "bg-[#0A66C2]/10 text-[#074A8C] border-[#074A8C]/30" },
    { name: "INDmoney",       url: `https://www.indmoney.com/search?query=${base}`,                  color: "bg-[#6366F1]/10 text-[#4347B5] border-[#4347B5]/30" },
    { name: "Groww",          url: `https://groww.in/stocks/search?q=${base}`,                       color: "bg-[#00B386]/10 text-[#008060] border-[#008060]/30" },
    { name: "Angel One",      url: `https://www.angelone.in/stocks/${base.toLowerCase()}-share-price`, color: "bg-[#F24242]/10 text-[#B02F2F] border-[#B02F2F]/30" },
    { name: "Zerodha",        url: `https://zerodha.com/markets/stocks/${base}/`,                    color: "bg-[#387ED1]/10 text-[#2860A5] border-[#2860A5]/30" },
  ];
};
